import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchRoundTables,
  fetchRoundTable,
  createRoundTable,
  speakInRoundTable,
  resolveRoundTable,
} from '../api/round-tables'
import type { RoundTableStatus, CreateRoundTableData, SpeakData, ResolveData } from '../types/round-tables'

export function useRoundTables(status?: RoundTableStatus) {
  return useQuery({
    queryKey: ['round-tables', status ?? 'all'],
    queryFn: () => fetchRoundTables(status),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function useRoundTable(id: string | null) {
  return useQuery({
    queryKey: ['round-tables', 'detail', id],
    queryFn: () => fetchRoundTable(id!),
    enabled: !!id,
    staleTime: 10_000,
    refetchInterval: 15_000,
  })
}

export function useCreateRoundTable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateRoundTableData) => createRoundTable(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round-tables'] })
    },
  })
}

export function useSpeakInRoundTable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SpeakData }) => speakInRoundTable(id, data),
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['round-tables', 'detail', id] })
    },
  })
}

export function useResolveRoundTable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ResolveData }) => resolveRoundTable(id, data),
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['round-tables'] })
      queryClient.invalidateQueries({ queryKey: ['round-tables', 'detail', id] })
    },
  })
}
